/**
 * Status timeline (design README shared kit) — a vertical rail of dated events, newest first.
 * Rendered as an ordered <ol> (a11y); each node carries a real <time datetime>. Two projections
 * share this component:
 *   - PUBLIC (tracking): only public events, no visibility markers, no actor identities.
 *   - STAFF (detail): full audit, public AND internal, each with a visibility marker + actor.
 * The caller decides which events to pass — the component never re-filters.
 */
import type { RequestStatus } from "@td/shared";
import type { CaseStatus, KORAStatus, PermitStatus, Visibility } from "../api/types.js";
import { StatusPill } from "./StatusPill.js";
import { KORAStatusPill } from "./KORAStatusPill.js";
import { CaseStatusPill } from "./CaseStatusPill.js";
import { PermitStatusPill } from "./PermitStatusPill.js";
import { VisibilityMarker } from "./VisibilityMarker.js";
import { absoluteDate, relativeTime } from "./format.js";

export interface TimelineNode {
  id: string;
  body: string;
  createdAt: string;
  toStatus?: RequestStatus;
  /** KORA-kind timeline nodes (records-request.md) carry a KORAStatus instead — its own disjoint
   *  value-space, never mixed with `toStatus`. */
  toKORAStatus?: KORAStatus;
  /** Case-kind timeline nodes (Code Enforcement) carry a CaseStatus instead — its own disjoint
   *  value-space, never mixed with `toStatus`/`toKORAStatus`. */
  toCaseStatus?: CaseStatus;
  /** Permit-kind timeline nodes (Permits & Licensing) carry a PermitStatus instead — its own
   *  disjoint value-space, never mixed with the others. */
  toPermitStatus?: PermitStatus;
  /** Staff timeline only. */
  visibility?: Visibility;
  actorName?: string | null;
}

export function StatusTimeline({
  nodes,
  showVisibility = false,
}: {
  nodes: TimelineNode[];
  showVisibility?: boolean;
}): JSX.Element {
  const ordered = [...nodes].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <ol className="td-timeline">
      {ordered.map((n) => (
        <li key={n.id} className="td-timeline__node">
          <span className="td-timeline__rail" aria-hidden="true">
            <span className="td-timeline__dot" />
          </span>
          <div className="td-timeline__content">
            <div className="td-timeline__head">
              {n.toStatus ? <StatusPill status={n.toStatus} size="sm" /> : null}
              {n.toKORAStatus ? <KORAStatusPill status={n.toKORAStatus} size="sm" /> : null}
              {n.toCaseStatus ? <CaseStatusPill status={n.toCaseStatus} size="sm" /> : null}
              {n.toPermitStatus ? <PermitStatusPill status={n.toPermitStatus} size="sm" /> : null}
              {showVisibility && n.visibility ? (
                <VisibilityMarker visibility={n.visibility} />
              ) : null}
              <time
                className="td-timeline__time"
                dateTime={n.createdAt}
                title={absoluteDate(n.createdAt)}
              >
                {relativeTime(n.createdAt)}
              </time>
            </div>
            <p className="td-timeline__body">{n.body}</p>
            {showVisibility && n.actorName ? (
              <p className="td-timeline__actor">— {n.actorName}</p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
